import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject, Subscription, throwError } from 'rxjs';
import { Observable } from 'rxjs/Observable';
import 'rxjs/add/observable/of';
import { 
  debounceTime, 
  switchMap, 
  filter, 
  tap, 
  catchError, 
  retryWhen, 
  delay, 
  take,
  groupBy,
  mergeMap
} from 'rxjs/operators';
import { isEqual } from 'lodash';
import { LetterService } from './letter.service';

// Declare Penmate global
declare const Penmate: any;

// Interfaces
export interface SaveOptions {
  priority?: 'critical' | 'normal' | 'low';
  trigger?: string;
  debounceMs?: number;
}

export interface SaveStatus {
  status: 'idle' | 'saving' | 'saved' | 'error';
  lastSaved?: Date;
  error?: any;
}

interface DraftSaveRequest {
  draft: any;
  params?: any;
  options?: SaveOptions;
  timestamp: number;
}

@Injectable({ providedIn: 'root' })
export class DraftManagerService {
  // Save queue for managing requests
  private saveQueue$ = new Subject<DraftSaveRequest>();
  
  // Track last saved state for deduplication
  private lastSavedDraft: any = null;
  
  // Currently pending save request
  private pendingSave: Subscription | null = null;
  
  // Save status for UI indicators
  private saveStatus$ = new BehaviorSubject<SaveStatus>({ 
    status: 'idle' 
  });

  // Priority-based debounce times (milliseconds)
  private readonly DEBOUNCE_TIMES = {
    critical: 0,      // Immediate
    normal: 1000,     // 1 second
    low: 3000         // 3 seconds
  };

  // Metrics tracking
  private metrics = {
    totalSaves: 0,
    duplicatesPrevented: 0,
    savesInProgress: 0,
    lastSaveTime: null as Date | null
  };

  constructor(private letterService: LetterService) {
    this.initializeSaveQueue();
  }

  /**
   * Check if running in development environment
   */
  private isDevelopment(): boolean {
    return typeof Penmate !== 'undefined' && Penmate.env && Penmate.env.name === 'development';
  }

  /**
   * Log message only in development environment
   */
  private log(...args: any[]): void {
    if (this.isDevelopment()) {
      console.log(...args);
    }
  }

  /**
   * Log error only in development environment
   */
  private logError(...args: any[]): void {
    if (this.isDevelopment()) {
      console.error(...args);
    }
  }

  /**
   * Initialize the save queue with intelligent processing
   */
  private initializeSaveQueue(): void {
    this.saveQueue$.pipe(
      // Group by priority
      groupBy(request => (request.options && request.options.priority) || 'normal'),
      
      // Apply priority-specific debouncing
      mergeMap(group$ => 
        group$.pipe(
          debounceTime(this.getDebounceTime(group$.key as string)),
          // Take only the latest request per priority group
          switchMap(request => Observable.of(request))
        )
      ),
      
      // Deduplicate
      filter(request => this.hasChanges(request.draft)),
      
      // Cancel previous pending save and execute new one
      switchMap(request => {
        this.cancelPending();
        return this.executeSave(request);
      }),
      
      // Update status
      tap(result => this.updateStatus(result))
    ).subscribe(
      result => {
        this.log('[DraftManager] Save completed', result);
      },
      error => {
        this.logError('[DraftManager] Save queue error', error);
        this.updateStatus({ error });
      }
    );
  }

  /**
   * Public method to save draft with intelligent queuing
   */
  saveDraft(draft: any, params?: any, options?: SaveOptions): void {
    // Check if draft.penmate is present before proceeding
    if (!draft || !draft.penmate) {
      this.log('[DraftManager] Save skipped - draft.penmate not present');
      return;
    }

    const request: DraftSaveRequest = {
      draft,
      params,
      options,
      timestamp: Date.now()
    };

    this.log(`[DraftManager] Save requested - priority: ${(options && options.priority) || 'normal'}`);
    
    this.saveQueue$.next(request);
  }

  /**
   * Bypass queue for critical saves that must complete immediately
   */
  saveImmediate(draft: any, params?: any): Observable<any> {
    this.log('[DraftManager] Immediate save requested');
    
    // Check if draft.penmate is present before proceeding
    if (!draft || !draft.penmate) {
      this.log('[DraftManager] Immediate save skipped - draft.penmate not present');
      return Observable.of(null);
    }
    
    // Cancel any pending saves
    this.cancelPending();
    
    // Update status
    this.saveStatus$.next({ status: 'saving' });
    
    return this.letterService.saveDraft(draft, params).pipe(
      tap(result => {
        this.updateLastSavedDraft(draft, result);
        this.updateStatus({ status: 'saved', lastSaved: new Date() });
        this.metrics.totalSaves++;
      }),
      catchError(error => {
        this.updateStatus({ status: 'error', error });
        return Observable.of({ error });
      })
    );
  }

  /**
   * Get observable save status for UI indicators
   */
  getSaveStatus(): Observable<SaveStatus> {
    return this.saveStatus$.asObservable();
  }

  /**
   * Cancel all pending saves
   */
  cancelPendingSaves(): void {
    this.cancelPending();
    this.saveStatus$.next({ status: 'idle' });
  }

  /**
   * Get current metrics for monitoring
   */
  getMetrics(): any {
    return { ...this.metrics };
  }

  /**
   * Force save - bypasses deduplication and saves immediately
   * Use this for critical saves that must always be sent to the server
   * @param draft - The draft data to save
   * @param params - Optional parameters for the save request
   * @returns Observable of the save result
   */
  forceSave(draft: any, params?: any): Observable<any> {
    this.log('[DraftManager] Force save requested - bypassing deduplication');
    
    // Check if draft.penmate is present before proceeding
    if (!draft || !draft.penmate) {
      this.log('[DraftManager] Force save skipped - draft.penmate not present');
      return Observable.of(null);
    }
    
    this.metrics.totalSaves++;
    this.metrics.savesInProgress++;
    this.updateStatus({ status: 'saving' });
    
    return this.letterService.saveDraft(draft, params).pipe(
      tap(result => {
        this.metrics.savesInProgress--;
        this.metrics.lastSaveTime = new Date();
        // Update last saved draft to prevent false positives in deduplication
        this.updateLastSavedDraft(draft, result);
        this.updateStatus({ status: 'saved', lastSaved: new Date() });
        this.log('[DraftManager] Force save completed successfully');
      }),
      catchError(error => {
        this.metrics.savesInProgress--;
        this.logError('[DraftManager] Force save failed:', error);
        this.updateStatus({ status: 'error', error });
        // Re-throw to allow component to handle error if needed
        return throwError(error);
      })
    );
  }

  /**
   * Check if draft has changes compared to last saved version
   */
  private hasChanges(newDraft: any): boolean {
    // Always save if no ID (need to create draft on server)
    if (!newDraft.id) {
      this.log('[DraftManager] New draft - will create');
      return true;
    }
    
    // For existing drafts, use deep equality check
    const hasChanges = !isEqual(this.lastSavedDraft, newDraft);
    
    if (!hasChanges) {
      this.log('[DraftManager] Skipping - no changes detected');
      this.metrics.duplicatesPrevented++;
    }
    
    return hasChanges;
  }

  /**
   * Get debounce time based on priority
   */
  private getDebounceTime(priority: string): number {
    return this.DEBOUNCE_TIMES[priority] || this.DEBOUNCE_TIMES.normal;
  }

  /**
   * Cancel any pending save operation
   */
  private cancelPending(): void {
    if (this.pendingSave) {
      this.log('[DraftManager] Cancelling pending save');
      this.pendingSave.unsubscribe();
      this.pendingSave = null;
      this.metrics.savesInProgress = Math.max(0, this.metrics.savesInProgress - 1);
    }
  }

  /**
   * Execute the save operation with retry logic
   */
  private executeSave(request: DraftSaveRequest): Observable<any> {
    // Check if draft.penmate is present before proceeding
    if (!request.draft || !request.draft.penmate) {
      this.log('[DraftManager] Execute save skipped - draft.penmate not present');
      this.updateStatus({ status: 'idle' });
      return Observable.of(null);
    }

    this.saveStatus$.next({ status: 'saving' });
    this.metrics.savesInProgress++;

    return this.letterService.saveDraft(request.draft, request.params).pipe(
      // Retry on network failure
      retryWhen(errors => 
        errors.pipe(
          tap(err => this.log('[DraftManager] Save failed, retrying...', err)),
          delay(2000),
          take(3)
        )
      ),
      tap(result => {
        this.updateLastSavedDraft(request.draft, result);
        this.metrics.totalSaves++;
        this.metrics.savesInProgress--;
        this.metrics.lastSaveTime = new Date();
        this.log(`[DraftManager] Save successful - trigger: ${(request.options && request.options.trigger) || 'unknown'}`);
      }),
      catchError(error => {
        this.logError('[DraftManager] Save failed after retries', error);
        this.metrics.savesInProgress--;
        this.updateStatus({ status: 'error', error });
        return Observable.of({ error });
      })
    );
  }

  /**
   * Update the last saved draft for deduplication
   */
  private updateLastSavedDraft(draft: any, serverResponse?: any): void {
    // If this was a new draft creation, include the server-generated ID
    if (!draft.id && serverResponse && serverResponse.id) {
      this.lastSavedDraft = {
        ...draft,
        id: serverResponse.id,
        // Include any other server-generated fields
        ...serverResponse
      };
    } else {
      // For updates, just track what we sent
      this.lastSavedDraft = { ...draft };
    }
  }

  /**
   * Update save status
   */
  private updateStatus(result: any): void {
    if (result.error) {
      this.saveStatus$.next({ 
        status: 'error', 
        error: result.error 
      });
    } else {
      this.saveStatus$.next({ 
        status: 'saved', 
        lastSaved: new Date() 
      });
      
      // Reset to idle after a short delay
      setTimeout(() => {
        if (this.saveStatus$.value.status === 'saved') {
          this.saveStatus$.next({ 
            status: 'idle',
            lastSaved: this.saveStatus$.value.lastSaved
          });
        }
      }, 2000);
    }
  }

  /**
   * Reset the service state (useful for testing)
   */
  reset(): void {
    this.cancelPendingSaves();
    this.lastSavedDraft = null;
    this.saveStatus$.next({ status: 'idle' });
    this.metrics = {
      totalSaves: 0,
      duplicatesPrevented: 0,
      savesInProgress: 0,
      lastSaveTime: null
    };
  }
}
